import React, { useRef, useCallback } from "react";
import { cn } from "utils/helpers";

const OTP_LENGTH = 8;

interface OtpInputProps {
  className?: string;
  disabled?: boolean;
  error?: string;
  value: string;
  onChange: (value: string) => void;
}

export const OtpInput: React.FC<OtpInputProps> = ({
  value,
  onChange,
  disabled = false,
  error,
  className,
}) => {
  const inputRefs = useRef<Array<HTMLInputElement | null>>([]);
  const clipboardSniffedRef = useRef(false);
  const cells = Array.from({ length: OTP_LENGTH }, (_, i) => value[i] ?? "");

  // First-focus clipboard sniff. Browsers gate `navigator.clipboard.readText()`
  // behind a user gesture — a mount-time call is silently denied (or worse,
  // grants in some contexts and clobbers an unrelated clipboard). Focusing
  // a cell IS a gesture, so the permission prompt actually fires and only
  // when the user has signalled intent to paste a code. Guarded by a ref so
  // we never re-sniff on subsequent focuses (and never overwrite a value the
  // user has already started typing).
  const handleFirstFocus = useCallback(() => {
    if (clipboardSniffedRef.current) {
      return;
    }
    clipboardSniffedRef.current = true;
    if (value.length > 0) {
      return;
    }
    if (typeof navigator === "undefined" || !navigator.clipboard?.readText) {
      return;
    }
    navigator.clipboard
      .readText()
      .then((text) => {
        const digits = (text ?? "").replace(/\D/g, "").slice(0, OTP_LENGTH);
        if (digits.length === OTP_LENGTH) {
          onChange(digits);
          // Move focus to the last cell so Enter/Submit is one key away.
          const target = inputRefs.current[OTP_LENGTH - 1];
          target?.focus();
        }
      })
      .catch(() => {
        // Permission denied / clipboard empty / unsupported — silent.
      });
  }, [onChange, value.length]);

  const focusCell = (index: number) => {
    const target = inputRefs.current[Math.max(0, Math.min(index, OTP_LENGTH - 1))];
    target?.focus();
  };

  const updateValue = useCallback(
    (index: number, char: string) => {
      const next = cells.slice();
      next[index] = char;
      onChange(next.join(""));
    },
    [cells, onChange]
  );

  const handleKeyDown = (index: number) => (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Backspace") {
      e.preventDefault();
      if (cells[index]) {
        updateValue(index, "");
      } else if (index > 0) {
        updateValue(index - 1, "");
        focusCell(index - 1);
      }
    } else if (e.key === "ArrowLeft" && index > 0) {
      focusCell(index - 1);
    } else if (e.key === "ArrowRight" && index < OTP_LENGTH - 1) {
      focusCell(index + 1);
    }
  };

  const handleChange = (index: number) => (e: React.ChangeEvent<HTMLInputElement>) => {
    const digit = e.target.value.replace(/\D/g, "").slice(-1);
    if (!digit) {
      return;
    }
    updateValue(index, digit);
    if (index < OTP_LENGTH - 1) {
      focusCell(index + 1);
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, OTP_LENGTH);
    if (!pasted) {
      return;
    }
    const next = Array.from({ length: OTP_LENGTH }, (_, i) => pasted[i] ?? "");
    onChange(next.join(""));
    const nextFocusIndex = Math.min(pasted.length, OTP_LENGTH - 1);
    focusCell(nextFocusIndex);
  };

  return (
    <div>
      <div
        className={cn(
          // Always-fluid: each cell shares the row width with flex-1 so
          // the 8-digit OTP fits any container — from a 320px mobile
          // viewport up to a wide desktop card — without the previous
          // sm: locked 40px overflowing narrow registration cards. The
          // max-w cap stops the boxes ballooning on very wide layouts.
          "flex w-full max-w-sm gap-1 sm:gap-2",
          className
        )}
        role="group"
        aria-label="One-time password"
      >
        {cells.map((cell, i) => (
          <input
            key={i}
            ref={(el) => {
              inputRefs.current[i] = el;
            }}
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            maxLength={1}
            value={cell}
            disabled={disabled}
            autoComplete={i === 0 ? "one-time-code" : "off"}
            aria-label={`Digit ${i + 1} of ${OTP_LENGTH}`}
            data-testid={`otp-input-${i}`}
            onChange={handleChange(i)}
            onKeyDown={handleKeyDown(i)}
            onPaste={handlePaste}
            onFocus={(e) => {
              handleFirstFocus();
              e.target.select();
            }}
            className={cn(
              // min-w-0 + flex-1 lets each cell shrink down to fit the
              // available width at every viewport. We deliberately do NOT
              // lock to a fixed sm:w-10 — inside the registration card
              // (max-w-md minus padding ≈ 384px) eight 40px boxes plus
              // gaps + borders overflow the right edge around 800px. A
              // max-w cap on the wrapper keeps the boxes from looking
              // ridiculous on very wide containers.
              "h-12 min-w-0 flex-1 rounded-md border text-center text-base font-semibold focus:outline-none focus:ring-2 focus:ring-blue-500 sm:text-lg",
              error
                ? "border-red-400 bg-red-50 text-red-700"
                : "border-gray-300 bg-white text-gray-900",
              disabled && "cursor-not-allowed opacity-50"
            )}
          />
        ))}
      </div>
      {error && (
        <p className="mt-1 text-xs text-red-600" role="alert">
          {error}
        </p>
      )}
    </div>
  );
};
